import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { useSubscription } from '@/contexts/useSubscription';
import { track } from '@/lib/analytics';
import OnboardingCompleteShell from '@/components/layout/OnboardingCompleteShell';
import MembershipCard from '@/components/subscription/MembershipCard';
import Button from '@/components/ui/Button';
import Confetti from '@/components/ui/Confetti';

const RETRY_DELAY_MS = 1500;

const ease = [0.22, 1, 0.36, 1] as const;

const PREMIUM_UNLOCKS = [
  'Unlimited mock interviews',
  'Full practice mode with all 128 civics questions',
  'Detailed per-question review and explanations',
];

const FREE_INCLUDES = [
  'Daily practice with civics questions',
  'Track your progress over time',
  'Upgrade anytime to unlock everything',
];

export default function OnboardingWelcomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const { isPremium, cadence, currentPeriodEnd, refreshSubscription } =
    useSubscription();

  const expectingPremium = searchParams.get('from') === 'premium';
  const sessionId = searchParams.get('session_id');

  const [resolving, setResolving] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const retriedRef = useRef(false);

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      await refreshSubscription();
      if (!active) return;

      if (expectingPremium && !retriedRef.current) {
        retriedRef.current = true;
        setTimeout(async () => {
          if (!active) return;
          await refreshSubscription();
          if (active) setResolving(false);
        }, RETRY_DELAY_MS);
        return;
      }

      setResolving(false);
    };

    resolve();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire confetti once per checkout session — not on refresh, not on revisit
  useEffect(() => {
    if (!isPremium || resolving || !sessionId) return;
    const key = `confetti_seen_${sessionId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 1700);
    return () => clearTimeout(timer);
  }, [isPremium, resolving, sessionId]);

  const fullName = profile?.full_name?.trim() || 'Member';
  const firstName = fullName.split(' ')[0];

  const renewDate =
    isPremium && currentPeriodEnd
      ? new Date(currentPeriodEnd).toLocaleDateString(undefined, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

  const memberSince = (() => {
    const source = profile?.created_at ?? user?.created_at;
    if (!source) return null;
    return new Date(source).toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
    });
  })();

  const planLabel =
    isPremium && cadence
      ? `Premium · ${cadence === 'annual' ? 'Annual' : 'Monthly'}`
      : 'Free Plan';

  const handleStart = () => {
    track('onboarding_completed', { converted_at_paywall: isPremium });
    navigate('/home', { replace: true });
  };

  const unlocks = isPremium ? PREMIUM_UNLOCKS : FREE_INCLUDES;
  const unlocksHeading = isPremium ? "What you've unlocked" : "What's included";

  return (
    <OnboardingCompleteShell>
      {showConfetti && <Confetti />}

      <div className="text-center">
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05, ease }}
          className="font-sans text-xs uppercase tracking-[0.2em] text-text-muted mb-3"
        >
          {firstName ? `Welcome, ${firstName}` : 'Welcome'}
        </motion.p>

        {/* Hero headline — outside the card, on cream */}
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease }}
          className="font-display italic font-light text-5xl md:text-6xl text-text-primary leading-[1.05] mb-8 md:mb-10"
        >
          {isPremium ? (
            <>
              You&apos;re <span className="text-primary">Premium.</span>
            </>
          ) : (
            <>
              You&apos;re <span className="text-primary">ready.</span>
            </>
          )}
        </motion.h1>

        {/* Membership card — the artifact */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease }}
        >
          <MembershipCard
            isPremium={isPremium}
            fullName={fullName}
            memberSince={memberSince}
            planLabel={planLabel}
          />
        </motion.div>

        {/* What you unlocked */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45, ease }}
          className="mt-8 md:mt-10 mx-auto max-w-[420px] text-left"
        >
          <div className="font-sans text-xs uppercase tracking-[0.2em] text-text-muted mb-3">
            {unlocksHeading}
          </div>
          <ul className="space-y-2.5">
            {unlocks.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-success/15 mt-0.5 shrink-0">
                  <Check size={12} strokeWidth={3} className="text-success" />
                </div>
                <span className="font-sans text-sm text-text-secondary leading-relaxed">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6, ease }}
          className="mt-8 md:mt-10 mx-auto max-w-[420px]"
        >
          <Button fullWidth onClick={handleStart} disabled={resolving}>
            {resolving
              ? 'Setting things up…'
              : isPremium
                ? 'Start your first practice'
                : 'Start practicing'}
          </Button>
        </motion.div>

        {/* Footer meta — combined into one quiet line */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: resolving ? 0 : 1 }}
          transition={{ duration: 0.4, delay: resolving ? 0 : 0.78 }}
          className="font-sans text-xs text-text-muted mt-6"
        >
          {isPremium && renewDate && <>Renews {renewDate}</>}
          {isPremium && renewDate && user?.email && (
            <span className="mx-2 text-text-muted/60">·</span>
          )}
          {user?.email && <>Receipt sent to {user.email}</>}
        </motion.p>
      </div>
    </OnboardingCompleteShell>
  );
}
